import { useState } from "react";
import Papa from "papaparse";

const MAX_ROWS = 1100;

export interface ParsedCsv {
  headers: string[];
  rows: Record<string, any>[];
}

export interface UseCsvOptions {
  // Called with a user-readable error when the file is invalid or too large.
  // Errors are reported externally so a single shared error surface in the
  // app can aggregate them with API/processing errors.
  onError: (msg: string) => void;
}

export function useCsv({ onError }: UseCsvOptions) {
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<Record<string, any>[]>([]);
  const [uploadedFileName, setUploadedFileName] = useState("");
  const [isDragOver, setIsDragOver] = useState(false);

  // Returns the parsed result on success so callers can chain follow-up
  // actions (auto-detect columns, reset progress, clear recovery) without
  // having to react via effects.
  function loadFile(file: File | null | undefined): Promise<ParsedCsv | null> {
    if (!file) return Promise.resolve(null);

    if (!file.name.toLowerCase().endsWith(".csv") && file.type !== "text/csv") {
      onError("Please select a CSV file");
      setUploadedFileName("");
      return Promise.resolve(null);
    }

    setUploadedFileName(file.name);

    return new Promise((resolve) => {
      Papa.parse<Record<string, any>>(file, {
        header: true,
        skipEmptyLines: true,
        dynamicTyping: false,
        complete: (res) => {
          const data = (res.data || []).filter(
            (r) => Object.keys(r).length > 0
          );

          if (data.length > MAX_ROWS) {
            onError(
              `File contains ${data.length} rows. Maximum allowed is ${MAX_ROWS.toLocaleString()} rows. Please reduce your file size and try again.`
            );
            setUploadedFileName("");
            resolve(null);
            return;
          }

          const headers =
            res.meta.fields || (data.length ? Object.keys(data[0]) : []);
          setCsvHeaders(headers);
          setRows(data);
          resolve({ headers, rows: data });
        },
        error: (err) => {
          onError(err.message);
          setUploadedFileName("");
          resolve(null);
        },
      });
    });
  }

  // Restore CSV state from a recovery snapshot without re-parsing.
  function setSnapshot(headers: string[], snapshotRows: Record<string, any>[]) {
    setCsvHeaders(headers);
    setRows(snapshotRows);
  }

  function reset() {
    setCsvHeaders([]);
    setRows([]);
    setUploadedFileName("");
    setIsDragOver(false);
  }

  return {
    csvHeaders,
    rows,
    uploadedFileName,
    isDragOver,
    setIsDragOver,
    loadFile,
    setSnapshot,
    reset,
  };
}
